/**
 * Layer 2: Capability Trust Engine (CTE) — POLISHED
 * Armadillo Adamantium Security Framework
 * 
 * Responsibility: Issue & verify capability tokens with cryptographic signatures.
 * Uses Ed25519 (production-ready) with optional upgrade to CRYSTALS-Dilithium (post-quantum).
 * 
 * Polish additions:
 * - Key rotation with overlapping validity windows
 * - Revocation lists with TTL-based expiry
 * - Short-lived refresh tokens with parent tracking
 * - Session versioning (bumping version invalidates older tokens)
 * - Route-bound capabilities (tokens valid only for specific routes)
 */

import nacl from 'tweetnacl';
import { encodeBase64, decodeBase64, encodeUTF8, decodeUTF8 } from 'tweetnacl-util';
import { sha256 } from 'js-sha256';
import { SecurityContext, CapabilityPayload, CapabilityToken, TrustTierName, KeyRotationState, SessionVersion } from './types';

// ============================================
// KEY MANAGEMENT
// ============================================

/**
 * Generate Ed25519 key pair for token signing
 * In production, store these in environment variables
 */
export function generateKeyPair(): { publicKey: string; privateKey: string } {
  const keyPair = nacl.sign.keyPair();
  return {
    publicKey: encodeBase64(keyPair.publicKey),
    privateKey: encodeBase64(keyPair.secretKey)
  };
}

/**
 * Get or create signing keys from environment
 */
function getSigningKeys(): { publicKey: Uint8Array; secretKey: Uint8Array } {
  // Try to get from environment variables
  const publicKeyEnv = typeof process !== 'undefined' ? (process as any).env?.ARMADILLO_PUBLIC_KEY : null;
  const privateKeyEnv = typeof process !== 'undefined' ? (process as any).env?.ARMADILLO_PRIVATE_KEY : null;
  
  if (publicKeyEnv && privateKeyEnv) {
    return {
      publicKey: decodeBase64(publicKeyEnv),
      secretKey: decodeBase64(privateKeyEnv)
    };
  }
  
  // Generate new keys if not provided (for development)
  const keyPair = nacl.sign.keyPair();

  
  return {
    publicKey: keyPair.publicKey,
    secretKey: keyPair.secretKey
  };
}

// ============================================
// TOKEN ISSUANCE
// ============================================

/**
 * Issue a capability token for a security context
 * Token is signed with Ed25519 and bound to session entropy
 */
export async function issueCapability(
  ctx: SecurityContext,
  allowedActions: string[],
  ttlSec: number = 300,  // 5 minutes default
  extras?: {
    routeBound?: string[];
    sessionVersion?: number;
    keyVersion?: string;
    parentTokenId?: string;
    refreshCount?: number;
  }
): Promise<CapabilityToken> {
  const now = Math.floor(Date.now() / 1000);
  
  // Build payload
  const payload: CapabilityPayload = {
    tokenId: crypto.randomUUID(),
    sub: ctx.sessionId,
    iat: now,
    exp: now + ttlSec,
    scope: allowedActions,
    riskBound: ctx.riskScore,
    entropyBinding: await computeEntropyBinding(ctx),
    routeBound: extras?.routeBound,
    sessionVersion: extras?.sessionVersion,
    keyVersion: extras?.keyVersion || keyRotationState.currentKeyId,
    parentTokenId: extras?.parentTokenId,
    refreshCount: extras?.refreshCount,
  };
  
  // Serialize payload
  const payloadJson = JSON.stringify(payload);
  
  // Sign with Ed25519
  const { secretKey } = getSigningKeys();
  const messageBytes = decodeUTF8(payloadJson);
  const signature = nacl.sign.detached(messageBytes, secretKey);
  
  // Build token
  const token: CapabilityToken = {
    payload: payloadJson,
    signature: encodeBase64(signature),
    raw: `${encodeBase64(messageBytes)}.${encodeBase64(signature)}`
  };
  
  return token;
}

/**
 * Compute entropy binding for token
 * Binds token to session-specific entropy (timing jitter, TLS session, etc.)
 */
async function computeEntropyBinding(ctx: SecurityContext): Promise<string> {
  // Combine multiple entropy sources
  const timing = ctx.timestamp % 1000; // Millisecond precision
  const tlsSessionId = ctx.headers?.['cf-tls-session-id'] || 'none';
  const entropyNonce = ctx.entropyNonce || 'none';
  
  const entropyString = `${ctx.sessionId}|${entropyNonce}|${tlsSessionId}|${timing}`;
  return await sha256(entropyString);
}

// ============================================
// TOKEN VERIFICATION
// ============================================

/**
 * Verify a capability token
 * Checks signature, expiry, scope, entropy binding, and IP/fingerprint binding
 */
export async function verifyToken(
  token: string,
  ctx: SecurityContext
): Promise<{ valid: boolean; payload?: CapabilityPayload; reason?: string }> {
  try {
    // Parse token
    const parts = token.split('.');
    if (parts.length !== 2) {
      return { valid: false, reason: 'Invalid token format' };
    }
    
    const payloadJson = decodeUTF8(decodeBase64(parts[0]));
    const signature = decodeBase64(parts[1]);
    const payload: CapabilityPayload = JSON.parse(payloadJson);
    
    // Verify signature
    const { publicKey } = getSigningKeys();
    const messageBytes = decodeUTF8(payloadJson);
    const isValid = nacl.sign.detached.verify(messageBytes, signature, publicKey);
    
    if (!isValid) {
      return { valid: false, reason: 'Invalid signature' };
    }
    
    // Check expiry
    const now = Math.floor(Date.now() / 1000);
    if (now > payload.exp) {
      return { valid: false, reason: 'Token expired' };
    }
    
    // Verify session binding
    if (payload.sub !== ctx.sessionId) {
      return { valid: false, reason: 'Session mismatch' };
    }
    
    // Verify entropy binding
    const expectedEntropy = await computeEntropyBinding(ctx);
    if (payload.entropyBinding !== expectedEntropy) {
      return { valid: false, reason: 'Entropy binding mismatch' };
    }
    
    // Verify risk bound
    if (ctx.riskScore > payload.riskBound) {
      return { valid: false, reason: 'Risk score exceeds bound' };
    }
    
    // Polish: Check revocation
    if (isTokenRevoked(payload.tokenId)) {
      return { valid: false, reason: 'Token has been revoked' };
    }
    
    // Polish: Check session version
    if (!isTokenVersionCurrent(payload)) {
      return { valid: false, reason: 'Session version superseded — re-authenticate' };
    }
    
    // Polish: Check route binding
    if (!isTokenValidForRoute(payload, ctx.path)) {
      return { valid: false, reason: 'Token not valid for this route' };
    }
    
    // Polish: Auto-rotate keys if due
    if (isKeyRotationDue()) {
      rotateKeys();
    }
    
    return { valid: true, payload };
    
  } catch (error) {
    return {
      valid: false,
      reason: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// ============================================
// TOKEN UTILITIES
// ============================================

/**
 * Check if token has required scope
 */
export function hasScope(token: CapabilityToken | null, requiredAction: string): boolean {
  if (!token) return false;
  
  try {
    const payload: CapabilityPayload = JSON.parse(token.payload);
    return payload.scope.includes(requiredAction);
  } catch {
    return false;
  }
}

/**
 * Get trust tier from token payload
 */
export function getTrustTierFromToken(token: CapabilityToken | null): TrustTierName {
  if (!token) return 'untrusted';
  
  try {
    const payload: CapabilityPayload = JSON.parse(token.payload);
    
    // Map risk bound to trust tier
    if (payload.riskBound >= 0.86) return 'armadillo';
    if (payload.riskBound >= 0.71) return 'elevated';
    if (payload.riskBound >= 0.51) return 'trusted';
    if (payload.riskBound >= 0.31) return 'verified';
    if (payload.riskBound >= 0.11) return 'probationary';
    return 'untrusted';
  } catch {
    return 'untrusted';
  }
}

/**
 * Refresh token before expiry
 * Extends TTL while maintaining scope and bindings
 */
export async function refreshToken(
  existingToken: CapabilityToken,
  ctx: SecurityContext,
  additionalTtlSec: number = 300
): Promise<CapabilityToken | null> {
  try {
    const payload: CapabilityPayload = JSON.parse(existingToken.payload);
    
    // Check if token is still valid (not expired)
    const now = Math.floor(Date.now() / 1000);
    if (now > payload.exp + 60) { // 60 second grace period
      return null;
    }
    
    // Issue new token with extended TTL
    return await issueCapability(ctx, payload.scope, additionalTtlSec);
  } catch {
    return null;
  }
}

/**
 * Revoke token (add to blacklist with TTL)
 * Revocation list auto-expires entries after their token would have expired
 */
interface RevocationEntry {
  tokenId: string;
  revokedAt: number;
  expiresAt: number;
  reason: string;
}

const tokenRevocationList = new Map<string, RevocationEntry>();
const MAX_REVOCATION_SIZE = 5000;

export function revokeToken(tokenId: string, reason: string = 'manual', expiresAt?: number): void {
  const now = Date.now();
  tokenRevocationList.set(tokenId, {
    tokenId,
    revokedAt: now,
    expiresAt: expiresAt || (now + 3600000), // Default 1h TTL on revocation entry
    reason,
  });
  
  // Cleanup expired entries when over limit
  if (tokenRevocationList.size > MAX_REVOCATION_SIZE) {
    const cutoff = now - 3600000;
    for (const [key, entry] of tokenRevocationList) {
      if (entry.expiresAt < cutoff) tokenRevocationList.delete(key);
    }
  }
}

export function isTokenRevoked(tokenId: string): boolean {
  const entry = tokenRevocationList.get(tokenId);
  if (!entry) return false;
  if (Date.now() > entry.expiresAt) {
    tokenRevocationList.delete(tokenId);
    return false;
  }
  return true;
}

export function getRevocationCount(): number {
  return tokenRevocationList.size;
}

// ============================================
// POLISH: KEY ROTATION
// ============================================

let keyRotationState: KeyRotationState = {
  currentKeyId: 'default-v1',
  rotatedAt: Date.now(),
  nextRotationAt: Date.now() + 86400000 * 7, // 7 days
  activeKeys: ['default-v1'],
};

const keyStore = new Map<string, { publicKey: Uint8Array; secretKey: Uint8Array }>();

/**
 * Rotate signing keys with overlapping validity window
 * Both old and new keys are valid during the overlap period
 */
export function rotateKeys(): KeyRotationState {
  const newKeyId = `key-${Date.now().toString(36)}`;
  const keyPair = nacl.sign.keyPair();
  
  keyStore.set(newKeyId, {
    publicKey: keyPair.publicKey,
    secretKey: keyPair.secretKey,
  });
  
  const previousKeyId = keyRotationState.currentKeyId;
  keyRotationState = {
    currentKeyId: newKeyId,
    previousKeyId,
    rotatedAt: Date.now(),
    nextRotationAt: Date.now() + 86400000 * 7,
    activeKeys: [newKeyId, ...(previousKeyId ? [previousKeyId] : [])],
  };
  
  // Purge keys older than 2 rotations (14 days)
  const cutoff = Date.now() - 86400000 * 14;
  for (const [keyId] of keyStore) {
    if (!keyRotationState.activeKeys.includes(keyId) && keyId !== 'default-v1') {
      keyStore.delete(keyId);
    }
  }
  
  return keyRotationState;
}

export function getKeyRotationState(): KeyRotationState {
  return { ...keyRotationState };
}

/**
 * Check if key rotation is due
 */
export function isKeyRotationDue(): boolean {
  return Date.now() >= keyRotationState.nextRotationAt;
}

// ============================================
// POLISH: SESSION VERSIONING
// ============================================

const sessionVersionMap = new Map<string, SessionVersion>();

/**
 * Bump session version — invalidates all tokens with lower version
 * Call this when a security event occurs (password change, 2FA enrollment, etc.)
 */
export function bumpSessionVersion(sessionId: string, reason: string): number {
  const current = sessionVersionMap.get(sessionId);
  const newVersion = (current?.version || 0) + 1;
  
  sessionVersionMap.set(sessionId, {
    sessionId,
    version: newVersion,
    updatedAt: Date.now(),
    reason,
  });
  
  // Cleanup old sessions (keep last 10000)
  if (sessionVersionMap.size > 10000) {
    const cutoff = Date.now() - 86400000 * 7;
    for (const [key, entry] of sessionVersionMap) {
      if (entry.updatedAt < cutoff) sessionVersionMap.delete(key);
    }
  }
  
  return newVersion;
}

export function getSessionVersion(sessionId: string): number {
  return sessionVersionMap.get(sessionId)?.version || 0;
}

// ============================================
// POLISH: ENHANCED TOKEN ISSUANCE WITH ROUTE BINDING
// ============================================

/**
 * Issue a route-bound capability token
 * Token is only valid for specific route patterns
 */
export async function issueRouteBoundCapability(
  ctx: SecurityContext,
  allowedActions: string[],
  routePatterns: string[],
  ttlSec: number = 120 // Short-lived for route-bound tokens
): Promise<CapabilityToken> {
  const sessionVersion = getSessionVersion(ctx.sessionId);
  
  return await issueCapability(ctx, allowedActions, ttlSec, {
    routeBound: routePatterns,
    sessionVersion,
    keyVersion: keyRotationState.currentKeyId,
  });
}

/**
 * Issue a refresh token (longer-lived, narrower scope)
 */
export async function issueRefreshToken(
  ctx: SecurityContext,
  parentTokenId: string,
  refreshCount: number = 0
): Promise<CapabilityToken> {
  // Refresh tokens have minimal scope and longer TTL
  return await issueCapability(ctx, ['auth:refresh'], 86400, {
    parentTokenId,
    refreshCount: refreshCount + 1,
    sessionVersion: getSessionVersion(ctx.sessionId),
    keyVersion: keyRotationState.currentKeyId,
  });
}

/**
 * Check if token is valid for a specific route
 */
export function isTokenValidForRoute(payload: CapabilityPayload, path: string): boolean {
  if (!payload.routeBound || payload.routeBound.length === 0) return true;
  return payload.routeBound.some(pattern => {
    if (pattern.endsWith('/*')) {
      return path.startsWith(pattern.slice(0, -2));
    }
    return path === pattern;
  });
}

/**
 * Check if token session version is current
 */
export function isTokenVersionCurrent(payload: CapabilityPayload): boolean {
  const currentVersion = getSessionVersion(payload.sub);
  return (payload.sessionVersion || 0) >= currentVersion;
}
